import { useMemo, useState } from 'react';

import { useOrganizationsList } from '../../../hooks/useApiQueries';
import { OrgSortColumn, Organization, SortOrder } from '../types';
import { filterOrganizations, sortOrganizations } from '../utils';

export const useOrganizations = () => {
  const { data: organizations = [], isLoading, refetch } = useOrganizationsList();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<OrgSortColumn>('id');
  const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleSort = (column: OrgSortColumn) => {
    const isAsc = sortColumn === column && sortOrder === 'ASC';
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setSortColumn(column);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredAndSorted = useMemo(() => {
    const filtered = filterOrganizations(organizations as Organization[], searchQuery);
    return sortOrganizations(filtered, sortColumn, sortOrder);
  }, [organizations, searchQuery, sortColumn, sortOrder]);

  const paginated = useMemo(() => {
    return filteredAndSorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredAndSorted, page, rowsPerPage]);

  return {
    organizations: paginated,
    totalCount: filteredAndSorted.length,
    totalRawCount: (organizations as Organization[]).length,
    isLoading,
    searchQuery,
    sortColumn,
    sortOrder,
    page,
    rowsPerPage,
    handleSort,
    handleSearchChange,
    handlePageChange,
    handleRowsPerPageChange,
    refetch,
  };
};
